import { TypedRouter } from './classes.typedrouter.js';
import * as plugins from './plugins.js';

export type IPostMethod = (
  typedRequestPostObject: plugins.typedRequestInterfaces.ITypedRequest,
  optionsArg?: ITypedTargetPostOptions
) => Promise<plugins.typedRequestInterfaces.ITypedRequest>;

/**
 * this is an alternative to a post url supplied in `new Typedrequest(new TypedTarget(...), 'someMethodName')`
 * enables the use of custom post functions
 * used for things like broadcast channels
 * e.g. @designestate/dees-comms
 * the main difference here is, that the response comes back async and is routed by interest through typedrouter
 */
export type IPostMethodWithTypedRouter = (
  typedRequestPostObject: plugins.typedRequestInterfaces.ITypedRequest,
  optionsArg?: ITypedTargetPostOptions
) => Promise<void> | Promise<plugins.typedRequestInterfaces.ITypedRequest>;

export interface ITypedTargetPostOptions {
  signal?: AbortSignal;
}

export interface ITypedTargetConstructorOptions {
  url?: string;
  postMethod?: IPostMethod;
  /**
   * a post method that does not return the answer
   */
  postMethodWithTypedRouter?: IPostMethodWithTypedRouter;
  /**
   * this typedrouter allows us to have easy async request response cycles
   */
  typedRouterRef?: TypedRouter<any>;
  /**
   * Declares that the configured post method observes optionsArg.signal and
   * releases its transport work when that signal aborts.
   */
  supportsAbortSignal?: boolean;
  /**
   * Bounds the wait for a separately routed response. Defaults to 30 seconds.
   */
  responseTimeoutMs?: number;
}

/**
 * a typed target defines a target for requests
 */
export class TypedTarget {
  url?: string;
  type?: 'rest' | 'socket';
  options: ITypedTargetConstructorOptions;
  private responseTimeoutMs: number;

  constructor(optionsArg: ITypedTargetConstructorOptions) {
    if (optionsArg.postMethodWithTypedRouter && !optionsArg.typedRouterRef) {
      throw new Error('you have to specify a typedrouter when using postmethod with typedrouter');
    }
    const responseTimeoutMs = optionsArg.responseTimeoutMs ?? 30_000;
    if (
      !Number.isSafeInteger(responseTimeoutMs) ||
      responseTimeoutMs <= 0 ||
      responseTimeoutMs > 2_147_483_647
    ) {
      throw new RangeError(
        'TypedTarget responseTimeoutMs must be a positive safe integer no greater than 2147483647'
      );
    }
    this.options = optionsArg;
    this.responseTimeoutMs = responseTimeoutMs;
  }

  /**
   * wether calls to this target are bound to the request/response cycle
   * if false, always delivers response as result of a call
   * if true, delivers response in a separate call
   * can only be async when type is 'socket'
   */
  public isAsync = false;

  public async post<T extends plugins.typedRequestInterfaces.ITypedRequest>(
    payloadArg: T,
    optionsArg: ITypedTargetPostOptions = {}
  ): Promise<T> {
    if (
      optionsArg.signal &&
      this.options.supportsAbortSignal !== true
    ) {
      throw new Error(
        'TypedTarget does not declare AbortSignal transport support'
      );
    }
    const postMethod = this.options.postMethod || this.options.postMethodWithTypedRouter;
    if (!postMethod) {
      throw new Error('TypedTarget requires a post method');
    }
    let responseInterest: plugins.lik.Interest<
      string,
      plugins.typedRequestInterfaces.ITypedRequest
    > | undefined;
    // having a typedrouter allows us to work with async request response cycles.
    if (this.options.typedRouterRef) {
      responseInterest = await this.options.typedRouterRef.fireEventInterestMap.addInterest(
        payloadArg.correlation!.id,
        payloadArg
      );
    }
    const signal = optionsArg.signal;
    if (signal?.aborted) {
      responseInterest?.destroy();
      throw signal.reason ?? new DOMException('The operation was aborted', 'AbortError');
    }

    let abortListener: (() => void) | undefined;
    const abortPromise = signal
      ? new Promise<never>((_resolve, reject) => {
          abortListener = () => {
            responseInterest?.destroy();
            reject(
              signal.reason ??
                new DOMException('The operation was aborted', 'AbortError')
            );
          };
          signal.addEventListener('abort', abortListener, { once: true });
        })
      : undefined;

    let responseTimeout:
      | ReturnType<typeof setTimeout>
      | undefined;
    const responseTimeoutPromise = responseInterest
      ? new Promise<never>((_resolve, reject) => {
          responseTimeout = setTimeout(() => {
            reject(
              new Error(
                `TypedTarget response timed out after ${this.responseTimeoutMs}ms`
              )
            );
          }, this.responseTimeoutMs);
          responseTimeout.unref?.();
        })
      : undefined;

    try {
      const postPromise = Promise.resolve(
        postMethod(payloadArg, optionsArg)
      );
      const postMethodReturnValue = await Promise.race([
        postPromise,
        ...(responseTimeoutPromise ? [responseTimeoutPromise] : []),
        ...(abortPromise ? [abortPromise] : []),
      ]);
      if (responseInterest) {
        return await Promise.race([
          responseInterest.interestFullfilled,
          responseTimeoutPromise!,
          ...(abortPromise ? [abortPromise] : []),
        ]) as T;
      }
      return postMethodReturnValue
        ? postMethodReturnValue as T
        : payloadArg;
    } finally {
      if (responseTimeout) {
        clearTimeout(responseTimeout);
      }
      if (signal && abortListener) {
        signal.removeEventListener('abort', abortListener);
      }
      responseInterest?.destroy();
    }
  }
}
